import type { FC } from 'react'
import { useState } from 'react'

import { Input, Textarea, Toggle, Tooltip } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { isOnlyDigits } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { usePolicyEditAccess, useUpdateProduct } from '@/modules/ecom-policy/policy/hooks'
import queries from '@/modules/ecom-policy/queries'
import { ecomPolicyResources } from '@/modules/ecom-policy/resources'

export const ProductSettingsTab: FC<{
  policyId: number
  productId: number
  isGroupedProduct?: boolean
  onClose: () => void
}> = ({ policyId, productId, isGroupedProduct = false, onClose }) => {
  const { data: policy } = useQuery(queries.policyDetails(policyId), () =>
    ecomPolicyResources.getPolicyDetails(policyId)
  )
  const { data } = useQuery(
    queries.policyProducts(policyId),
    () => {
      if (!policy) throw Error('Policy data is missing!')
      return ecomPolicyResources.getPolicyProducts(policyId, policy.vendorId)
    },
    { enabled: !!policy }
  )
  const product = data?.products.find(p => p.id === productId)
  if (!product) return null

  const hasEditAccess = usePolicyEditAccess()
  const [quantityLimitValue, setQuantityLimitValue] = useState<number | ''>(
    product.quantityLimit || 0
  )
  const [isProductMandatory, setIsProductMandatory] = useState(Boolean(product.isMandatory))
  const [productAlias, setProductAlias] = useState(product.alias || product.name)
  const [productAliasDescription, setProductAliasDescription] = useState(
    product.aliasDescription || product.desc || ''
  )
  const { updateProduct, isUpdating } = useUpdateProduct()
  const translate = useTranslation()

  const updatedProductAlias = !productAlias || productAlias === product.name ? null : productAlias
  const updatedProductAliasDescription =
    !productAliasDescription || productAliasDescription === product.desc
      ? null
      : productAliasDescription

  const onSelectProduct = () => {
    if (!hasEditAccess) {
      return
    }
    updateProduct({
      policyId,
      payload: {
        products: [
          {
            productId,
            isMandatory: isProductMandatory,
            priceId: product.priceId || null,
            isSelected: true,
            quantityLimit: quantityLimitValue || 1,
            alias: updatedProductAlias,
            aliasDescription: updatedProductAliasDescription,
          },
        ],
      },
    })
  }
  const onRemoveProduct = () => {
    if (!hasEditAccess) {
      return
    }
    updateProduct(
      {
        policyId,
        payload: {
          products: [
            {
              productId: productId,
              isSelected: false,
              priceId: product.priceId || null,
              isMandatory: Boolean(product.isMandatory),
              quantityLimit: quantityLimitValue || 1,
              alias: updatedProductAlias,
              aliasDescription: updatedProductAliasDescription,
            },
          ],
        },
      },
      {
        onSuccess: onClose,
      }
    )
  }

  return (
    <>
      <Input
        isFullWidth
        id="alias"
        label={<Translation id="Product alias" />}
        marginBottom={16}
        value={productAlias}
        isDisabled={!hasEditAccess}
        onChange={e => setProductAlias(e.target.value)}
      />

      <Textarea
        isFullWidth
        id="aliasDescription"
        label={<Translation id="Product description alias" />}
        marginBottom={16}
        value={productAliasDescription}
        isDisabled={!hasEditAccess}
        onChange={e => setProductAliasDescription(e.target.value)}
      />

      <Input
        isAllTextSelectedOnFocus
        type="numeric"
        isFullWidth
        id="quantity-limit"
        label={<Translation id="Quantity limit" />}
        marginBottom={16}
        value={quantityLimitValue}
        isDisabled={!hasEditAccess}
        onChange={e => {
          const val = e.target.value
          if (val === '') setQuantityLimitValue('')
          if (!isOnlyDigits(val)) return
          const newValue = Number(val)
          if (newValue === 0) return
          setQuantityLimitValue(newValue)
        }}
      />

      {/* todo: remove important when popup is refactored */}
      <h5 className="!mb-2 !mt-4 text-sm font-normal">
        <Translation id="Decide whether the product is selected by default while making a new order and cannot be deselected." />
      </h5>

      <Toggle
        id="is-mandatory"
        label={<Translation id="Is mandatory" />}
        checked={isProductMandatory}
        isDisabled={!hasEditAccess}
        onChange={() => setIsProductMandatory(!isProductMandatory)}
      />

      <footer className="mt-4 flex items-center justify-end">
        {!isGroupedProduct && (
          <Button variant="tertiary" className="mr-0" onClick={onClose}>
            <Translation id="Cancel" />
          </Button>
        )}

        <Button
          variant="primary"
          isLoading={isUpdating}
          disabled={!hasEditAccess}
          onClick={e => {
            e.preventDefault()
            onSelectProduct()
          }}
        >
          <Translation id="Submit" />
        </Button>

        {Boolean(product.isMandatory) && (
          <Tooltip
            id={`remove-button-tooltip-${name}-${productId}`}
            style={{ zIndex: 100 }}
            size="large"
          />
        )}
        <span
          data-tooltip-id={`remove-button-tooltip-${name}-${productId}`}
          data-tooltip-content={translate(
            `A mandatory product cannot be removed. To enable removing, change the "Is mandatory" option and save`
          )}
        >
          <Button
            variant="danger"
            Icon={<></>}
            onClick={e => {
              e.preventDefault()
              onRemoveProduct()
            }}
            disabled={!hasEditAccess || isUpdating || Boolean(product.isMandatory)}
          >
            <Translation id="Remove" />
          </Button>
        </span>
      </footer>
    </>
  )
}
